import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import printIcon from "../../images/icons/print-icon.png";
import arrowLeftIcon from "../../images/icons/arrow-left-icon.png";
import { client } from "../../hooks/Client";
import Location from "../../hooks/Location";
import fetchPublicIntakeForm from "../../helpers/intakeForms/fetchPublicIntakeForm";
import RequiredMark from "../../components/open/RequiredMark";
import getColumnType from "../../helpers/intakeForms/getColumnType";

/**
 * Het printvel van één intakeformulier: dezelfde vragen als het digitale
 * formulier, met ruimte om ze met de hand in te vullen.
 *
 * WAAROM DEZELFDE BRON ALS HET INVULSCHERM: het vel komt uit
 * `getPublicIntakeForm`, precies zoals `IntakeFormOpen` dat doet. Een eigen
 * Firestore-lees hier zou een tweede lezing van hetzelfde formulier zijn, en
 * die twee lopen uiteen zodra er een veldtype bijkomt — een geprint formulier
 * dat niet klopt met het digitale is erger dan geen print.
 *
 * Deze pagina zit achter een login, maar leest bewust via de publieke functie:
 * die geeft het formulier al samengesteld terug, inclusief de antwoordopties en
 * de matrixdefinities.
 */
const PrintIntakeForm = () => {

    // State
    /** @type {ReturnType<typeof useState<object|null>>} */
    const [formData, setFormData] = useState(/** @type {object|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // Hooks
    const history = useHistory();

    // Url parameters
    const formId = Location()[3];

    // Firestore (via de publieke functie)
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setLoadError('');

            try {
                const payload = await fetchPublicIntakeForm({ client, formId });
                if (cancelled) return;
                setFormData(payload);
            } catch (err) {
                if (cancelled) return;
                setFormData(null);
                setLoadError(
                    err?.code === 'not-found'
                        ? 'Dit formulier bestaat niet (meer).'
                        : 'Het formulier kon niet worden opgehaald. Ververs de pagina of probeer het later opnieuw.'
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [formId]);

    const fields = formData?.fields || [];

    /**
     * De antwoordruimte per veldtype.
     *
     * Elk type uit `INTAKE_FORM_FIELD_TYPES` heeft hier een tak. Een type
     * zonder tak zou een vraag opleveren zonder plek om te antwoorden, en dat
     * valt op papier niet op — `PrintIntakeForm.test.jsx` loopt daarom over de
     * hele lijst.
     *
     * @param {{ID: string, Title: string, Type: string}} field - Veld uit de publieke payload
     * @returns {React.ReactNode}
     */
    const renderAnswerSpace = (field) => {
        const options = formData?.options?.[field.ID] || [];

        switch (field.Type) {
            // Eén regel is genoeg voor een naam, een getal of een datum; een
            // groot vlak nodigt uit tot proza dat bij het overtypen niet in het
            // digitale veld past.
            case 'text':
            case 'number':
            case 'date':
                return <div className="print-intakeform-answer print-intakeform-answer--line" />;

            case 'notitie':
                return <div className="print-intakeform-answer print-intakeform-answer--block" />;

            // Rondjes bij één antwoord, vierkantjes bij meerdere: hetzelfde
            // onderscheid dat het scherm met radio's en vinkjes maakt.
            case 'multiple-one':
            case 'multiple-many':
                if (options.length === 0) {
                    return (
                        <p className="print-intakeform-answer print-intakeform-note">
                            Bij deze vraag staan nog geen antwoordopties.
                        </p>
                    );
                }
                return (
                    <ul className="print-intakeform-answer print-intakeform-options">
                        {options.map(option => (
                            <li key={option.ID}>
                                <span
                                    className={`print-intakeform-box print-intakeform-box--${field.Type === 'multiple-one' ? 'round' : 'square'}`}
                                    aria-hidden="true"
                                />
                                <span>{option.Option}</span>
                            </li>
                        ))}
                    </ul>
                );

            case 'checkbox':
                return (
                    <ul className="print-intakeform-answer print-intakeform-options">
                        <li>
                            <span className="print-intakeform-box print-intakeform-box--square" aria-hidden="true" />
                            <span>Ja</span>
                        </li>
                        <li>
                            <span className="print-intakeform-box print-intakeform-box--square" aria-hidden="true" />
                            <span>Nee</span>
                        </li>
                    </ul>
                );

            // Een bestand past niet op papier. De regel staat er wél, anders
            // ontbreekt de vraag op het vel en weet niemand dat er nog iets
            // aangeleverd moet worden.
            case 'upload':
                return (
                    <p className="print-intakeform-answer print-intakeform-note">
                        Hier hoort een document bij. Dat kan op papier niet — lever het los aan,
                        of vul deze vraag later in de app in.
                    </p>
                );

            case 'matrix': {
                const { rows = [], columns = [] } = formData?.matrix?.[field.ID] || {};

                if (rows.length === 0 || columns.length === 0) {
                    return (
                        <p className="print-intakeform-answer print-intakeform-note">
                            Deze tabel heeft nog geen rijen of kolommen.
                        </p>
                    );
                }

                return (
                    <table className="print-intakeform-answer print-intakeform-matrix">
                        <thead>
                            <tr>
                                <th />
                                {columns.map(column => (
                                    <th key={column.ID}>{column.Title}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.ID}>
                                    <th scope="row">{row.Title}</th>
                                    {columns.map(column => (
                                        // Het kolomtype staat erbij: bij een keuzekolom is
                                        // anders niet te zien dat er uit een vaste lijst
                                        // gekozen moet worden.
                                        <td key={column.ID} data-column-type={getColumnType(column)} />
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            }

            // Een veld waarvan het type nog niet gekozen is. Niet weglaten: dan
            // mist de vraag op het vel zonder dat iemand dat ziet.
            default:
                return <div className="print-intakeform-answer print-intakeform-answer--line" />;
        }
    };

    // JSX
    return (
        <div className="main">
            <div className="main-container">
                <div className="print-go-back-container" onClick={() => history.push(`/${client}/intakeforms`)}>
                    <img src={arrowLeftIcon} alt="" />
                    <p>Terug</p>
                </div>
                <div className="print-icon-container" onClick={() => window.print()}>
                    <img src={printIcon} alt="print icon" className="print-icon" />
                    <p>Print formulier</p>
                </div>

                <div id="print-intakeform-container">
                    {loading && (
                        <p className="print-intakeform-status">Het formulier wordt opgehaald…</p>
                    )}

                    {!loading && loadError && (
                        <p className="print-intakeform-status" role="alert">{loadError}</p>
                    )}

                    {!loading && !loadError && formData && (
                        <>
                            <div className="print-intakeform-header">
                                <h2>{formData.form?.Title || 'Formulier zonder titel'}</h2>
                                {formData.form?.Description && <p>{formData.form.Description}</p>}
                            </div>

                            {/* Pas ná het laden: 'nog geen vragen' is een bewering,
                                en die is onwaar zolang de gegevens onderweg zijn.
                                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                            {fields.length === 0 ? (
                                <p className="print-intakeform-status">
                                    Dit formulier heeft nog geen vragen.
                                </p>
                            ) : (
                                <div className="print-intakeform-fields">
                                    {fields.map(field => (
                                        <div key={field.ID} className="print-intakeform-field">
                                            <h3>
                                                {field.Title}
                                                <RequiredMark required={field.Required} />
                                            </h3>
                                            {field.Description && (
                                                <p className="print-intakeform-field-description">{field.Description}</p>
                                            )}
                                            {renderAnswerSpace(field)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PrintIntakeForm;
